import { fromJS } from 'immutable';
import { capitalize, get } from 'lodash';
import { getSetting } from '@utils/helpers';
import { v4 as uuidv4 } from 'uuid';
import { getDictionaries as getStatusOpDictionaries } from '@pages/DataBase/Status/IndicatorType/utils';

export const getPredefinedFilter = filterId => {
  return get(getSetting('predefinedFilters'), filterId);
};
export const getPredefinedColumns = columnId => {
  return get(getSetting('columnsRenderer'), columnId);
};

export const produceEntityFilter = (
  { searchAttr, entityValue = 'logicalId', filterDisplayName, filterValue = 'name', scope = 'internal' },
  entity
) => {
  return {
    search: { attr: searchAttr, value: entity[entityValue] },
    filter: {
      desc: filterDisplayName,
      fnValue: me => me.get('entity').get(filterValue),
    },
    entity,
    scope,
  };
};
export const produceListFilter = ({ searchAttr, filterDisplayName, scope = 'internal' }, list) =>
  fromJS({
    search: { attr: searchAttr, value: list },
    filter: {
      desc: filterDisplayName,
      fnValue: me => me.getIn(['search', 'value']).toJS().toString(),
    },
    scope,
  });

// TODO: Test this properly
// DO NOT trust this for anything else than maps pre filtering
export const produceHierarchyFilter = ({ filterDisplayName, scope = 'external', searchAttr, settingAttr }, where) =>
  fromJS({
    search: {
      attr: '$and',
      value: {
        relationLid: where.itemRelationName,
        [searchAttr]: where[settingAttr],
      },
    },
    filter: {
      desc: filterDisplayName,
      fnValue: me => {
        const searchValue = me.getIn(['search', 'value']);
        let id = searchValue.get('parentElementId') || searchValue.get('childElementId');
        const relationLid = searchValue.get('relationLid');
        let out = `${id}`;
        if (relationLid) out += ` ${props.t('with relation')}: ${relationLid}`;
        return out;
      },
    },
    scope,
  });

export const produceBoolFilter = ({ searchAttr, filterDisplayName, scope = 'internal' }, value) =>
  fromJS({
    search: { attr: searchAttr, value },
    filter: {
      desc: filterDisplayName,
      fnValue: me => '',
    },
    scope,
  });

export const produceStatusFilter = ({ filterDisplayName, scope = 'internal' }, value) =>
  fromJS({
    search: value,
    filter: {
      desc: filterDisplayName,
      fnValue: me => {
        const { opsDict } = getStatusOpDictionaries('operator');
        const displayFilters = me.get('search').map(indFilter => {
          const displayOpType = opsDict[indFilter.getIn(['operation', 'type'])].display;
          const displayOpValue = indFilter.getIn(['operation', 'value']);
          return `${indFilter.get('name')} ${displayOpType} ${displayOpValue}`;
        });
        return displayFilters.toJS();
      },
    },
    scope,
  });

export const getFilterNeeds = predefinedFilter => {
  // return needs
  let entityFilterKeys = Object.keys(predefinedFilter).filter(key =>
    filterDescs[key] ? filterDescs[key].type == 'entity' : false
  );
  let needs = [];
  for (let key of entityFilterKeys) {
    let filterValue = predefinedFilter[key]; // json
    let description = filterDescs[key]; //extra data for the filter
    // dataPath needs the uuid because actions are not being in sync when
    // changing from one tableV3 to another (the new fetch goes before)
    // end of "deleteResource" action
    const field = filterDescs[key].data.entityFetchValue;
    const data = {
      where: field == 'customId' ? { customId: filterValue } : { logicalId: filterValue },
    };
    needs.push({
      url: description.meta.url,
      dataPath: `predefinedFilter_${key}_${uuidv4()}`,
      data,
    });
  }
  return needs;
};

export const buildAdvancedSearchState = (predefinedFilter, entities) => {
  // entities -> Immutable array of received "needs"
  // call producers
  let advancedSearchState = {};
  Object.keys(predefinedFilter).forEach(filterKey => {
    const filterDesc = filterDescs[filterKey];
    if (!filterDesc) return;
    let filterValue = predefinedFilter[filterKey];

    switch (filterDesc.type) {
      case 'entity':
        // get the entity
        const entity = entities && entities.get(`predefinedFilter_${filterKey}`);
        if (!entity) return;
        advancedSearchState[filterDesc.data.key] = produceEntityFilter(filterDesc.data, entity);
        break;
      case 'list':
        advancedSearchState[filterDesc.data.key] = produceListFilter(filterDesc.data, filterValue);
        break;
      case 'hierarchy':
        advancedSearchState[filterDesc.data.key] = produceHierarchyFilter(filterDesc.data, filterValue);
        break;
      case 'bool':
        if (filterValue == false) break;
        advancedSearchState[filterDesc.data.key] = produceBoolFilter(filterDesc.data, filterValue);
        break;
      default:
    }
  });
  return advancedSearchState;
};
// FilterDesc: {type:<string>, meta:<any>, data:<object>}
export const filterDescs = {
  //entityFetchValue: field which the data provided by the user belong to. Used to bring the entity
  //entityValue: field which data will be used in the final query
  //eg: user will set a filter using customId of an itemType
  // then we need to find the itemType via customId,
  // but items has a fk to itemType logicalId so in the filter we specify "logicalId"
  itemType: {
    type: 'entity',
    meta: {
      url: '/api/itemTypes',
    },
    data: {
      key: 'itemType',
      searchAttr: 'elementTypeId',
      entityValue: 'logicalId',
      entityFetchValue: 'customId',
      filterDisplayName: 'Item type',
    },
  },
  currentLocation: {
    type: 'entity',
    meta: {
      url: '/api/v2/items',
    },
    data: {
      key: 'xlocs',
      searchAttr: 'parentElementId',
      filterDisplayName: 'Current location',
      entityValue: 'logicalId',
      entityFetchValue: 'logicalId',
      scope: 'external',
    },
  },
  labels: {
    type: 'list',
    data: {
      key: 'tag',
      searchAttr: 'name',
      filterDisplayName: 'Labels',
      scope: 'external',
    },
  },
  eventItem: {
    type: 'entity',
    meta: {
      url: '/api/v2/items',
    },
    data: {
      key: 'xEventItem',
      searchAttr: 'itemId',
      filterDisplayName: 'Events of item',
      entityValue: 'logicalId',
      entityFetchValue: 'customId',
      scope: 'external',
    },
  },
  childrenOfTheItem: {
    type: 'hierarchy',
    meta: {
      url: '/api/v2/items',
    },
    data: {
      key: 'pxhier',
      searchAttr: 'parentElementId',
      settingAttr: 'parentElementCustomId',
      filterDisplayName: 'Children of item',
      entityValue: 'customId',
      scope: 'external',
    },
  },
  parentsOfTheItem: {
    type: 'hierarchy',
    meta: {
      url: '/api/v2/items',
    },
    data: {
      key: 'cxhier',
      searchAttr: 'childElementId',
      settingAttr: 'childElementCustomId',
      filterDisplayName: 'Parents of item',
      entityValue: 'customId',
      scope: 'external',
    },
  },
};
// BAD, USE SINGLE SOURCE ...
const FLAVORS = ['asset', 'device', 'group', 'livestock', 'location', 'person', 'process', 'vehicle', 'wipPart', 'itemInCustody'];
FLAVORS.forEach(flavorName => {
  filterDescs[`is${capitalize(flavorName)}`] = {
    type: 'bool',
    data: {
      key: `${flavorName}.isAlive`,
      searchAttr: 'isAlive',
      filterDisplayName: `Is ${flavorName}`,
      scope: 'external',
    },
  };
});
